import { TextLink } from '@edsn/pie-components';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { FormCheckboxType } from '#pie/components/form/types';
import type { CreateDataRequestDto } from '#pie/internal/api/pie-bff';
import {
  Answer,
  DataRequestReceiver,
  LegalBasisPersonalDataReason,
  ReasonForDataRequest,
} from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

const { ConditionalField, FormCheckbox } =
  typedFormFields<CreateDataRequestDto>();

export const DataRequestTo = () => {
  //Voor welke netbeheerder(s) is dit dataverzoek?
  const { t } = useTranslation('dataRequest');
  const { t: thl } = useTranslation('hyperlinks');

  const options: FormCheckboxType[] = [
    {
      label: t(`data_request_receiver.${DataRequestReceiver.AllGridOperators}`),
      value: DataRequestReceiver.AllGridOperators,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.Coteq}`),
      value: DataRequestReceiver.Coteq,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.Enexis}`),
      value: DataRequestReceiver.Enexis,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.Liander}`),
      value: DataRequestReceiver.Liander,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.Rendo}`),
      value: DataRequestReceiver.Rendo,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.Stedin}`),
      value: DataRequestReceiver.Stedin,
    },
    {
      label: t(`data_request_receiver.${DataRequestReceiver.WestlandInfra}`),
      value: DataRequestReceiver.WestlandInfra,
    },
  ];

  const { watch, setValue } = useFormContext<CreateDataRequestDto>();
  const gridOperatorData = watch('dataRequestTo');

  useEffect(() => {
    if (
      gridOperatorData?.[gridOperatorData.length - 1] ===
      DataRequestReceiver.AllGridOperators
    ) {
      /* istanbul ignore next -- @preserve */
      if (
        !gridOperatorData.includes(DataRequestReceiver.AllGridOperators) ||
        gridOperatorData.length !== 1
      ) {
        setValue('dataRequestTo', [DataRequestReceiver.AllGridOperators]);
      }
    } else if (
      gridOperatorData?.includes(DataRequestReceiver.AllGridOperators)
    ) {
      const updatedValue = gridOperatorData.filter(
        (value) => value !== DataRequestReceiver.AllGridOperators,
      );
      if (updatedValue.length !== gridOperatorData.length) {
        setValue('dataRequestTo', updatedValue);
      }
    }
  }, [gridOperatorData, setValue]);

  const CreateFormCheckbox = () => {
    return (
      <FormCheckbox
        label={t('data_request_to')}
        options={options}
        name="dataRequestTo"
        infoTooltip={
          <>
            {t('link.see')}
            <TextLink
              href={thl('netbeheer_nederland.members')}
              target="_blank noreferror noopener"
            >
              {t('link.here')}
            </TextLink>{' '}
            {t('data_request_to.tooltip.part_two')}
          </>
        }
        rules={{ required: true }}
      />
    );
  };

  return (
    <ConditionalField
      field="dataRequestTo"
      condition={(fieldValue) => {
        return (
          (!!fieldValue('reason') &&
            fieldValue('reason') !== ReasonForDataRequest.Other &&
            fieldValue('reason') !== ReasonForDataRequest.LegalObligation &&
            fieldValue('reason') !== ReasonForDataRequest.OptimiseBulkUse) ||
          (!!fieldValue('reasonNote') &&
            fieldValue('reasonNote')!.length > 4) ||
          (!!fieldValue('informationIsPersonalData') &&
            fieldValue('informationIsPersonalData') === Answer.No) ||
          (!!fieldValue('legalBasisPersonalData') &&
            fieldValue('legalBasisPersonalData') === Answer.DontKnow) ||
          (!!fieldValue('legalBasisPersonalDataReason') &&
            fieldValue('legalBasisPersonalDataReason') !==
              LegalBasisPersonalDataReason.LegitimateInterest &&
            fieldValue('legalBasisPersonalDataReason') !==
              LegalBasisPersonalDataReason.Other) ||
          (!!fieldValue('legalBasisPersonalDataReasonNote') &&
            fieldValue('legalBasisPersonalDataReasonNote')!.length > 2)
        );
      }}
    >
      <CreateFormCheckbox />
    </ConditionalField>
  );
};
